import React, { FC, useEffect } from 'react';

import Header from './Header';
import Sidebar from './Sidebar';
import { MobileNavProvider, useMobileNav } from 'src/contexts/MobileNavContext';
import { ModalOpenProvider, useModalOpen } from 'src/contexts/ModalOpenContext';

import {
  StyledAppShell,
  StyledContent,
  StyledMainRow,
  StyledSidebarBackdrop,
} from './styled';

type MainLayoutProps = {
  Component?: FC;
  children?: React.ReactNode;
};

const MainLayoutInner: FC<MainLayoutProps> = ({ Component, children }) => {
  const { isSidebarOpen, closeSidebar } = useMobileNav();
  const { isModalOpen } = useModalOpen();

  // On mobile, close the drawer when a modal opens so it doesn't sit under the overlay.
  // Keep the sidebar mounted on desktop so the layout does not jump.
  useEffect(() => {
    if (isModalOpen) {
      closeSidebar();
    }
  }, [isModalOpen, closeSidebar]);

  return (
    <StyledAppShell>
      <Header />
      <StyledMainRow>
        <StyledSidebarBackdrop
          $visible={isSidebarOpen}
          onClick={closeSidebar}
          aria-hidden={!isSidebarOpen}
        />
        <Sidebar isOpen={isSidebarOpen} onNavigate={closeSidebar} />
        <StyledContent>{children ?? (Component ? <Component /> : null)}</StyledContent>
      </StyledMainRow>
    </StyledAppShell>
  );
};

const MainLayout: FC<MainLayoutProps> = ({ Component, children }) => (
  <ModalOpenProvider>
    <MobileNavProvider>
      <MainLayoutInner Component={Component}>{children}</MainLayoutInner>
    </MobileNavProvider>
  </ModalOpenProvider>
);

export default MainLayout;
